import { useState, useEffect, useCallback } from "react";
import {
  FaBolt,
  FaHashtag,
  FaWandMagicSparkles,
  FaCheck,
  FaShuffle,
  FaSpinner,
} from "react-icons/fa6";
import { FaExclamationTriangle } from "react-icons/fa";
import { ModalWraper } from "../../../../components/modal/wraper";
import { SelectVoucherProfile } from "../../../../components/forms/selects/hotpsot/SelectVoucherProfile";
import { VoucherService } from "../../../../services/hotspot/VoucherService";
import { SelectPop } from "../../../../components/forms/selects/SelectPop";
import { SelectNas } from "../../../../components/forms/selects/SelectNas";

// ==================== SUB COMPONENTS ====================

const FormField = ({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  required,
  icon: Icon,
  suffix,
  disabled,
  min,
  max,
}) => (
  <div>
    <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
      {Icon && <Icon className="text-[10px] text-slate-500" />}
      {label}
      {required && <span className="text-red-400">*</span>}
    </label>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  </div>
);

const CharsetToggle = ({ label, example, enabled, onToggle }) => (
  <button
    type="button"
    onClick={() => onToggle(!enabled)}
    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-medium transition-all ${
      enabled
        ? "ring-1 ring-cyan-500/30 bg-cyan-500/8 text-cyan-300"
        : "text-slate-400 hover:text-slate-200 bg-slate-800/40 hover:bg-slate-700/40"
    }`}
    style={{
      border: enabled
        ? "1px solid rgba(6,182,212,0.2)"
        : "1px solid rgba(51,65,85,0.3)",
    }}
  >
    <div
      className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${enabled ? "bg-cyan-600" : "bg-slate-700 border border-slate-600"}`}
    >
      {enabled && <FaCheck className="text-[8px] text-white" />}
    </div>
    <div className="text-left">
      <div className="font-semibold">{label}</div>
      <div className="text-[9px] opacity-60 font-mono">{example}</div>
    </div>
  </button>
);

// ==================== MAIN COMPONENT ====================

export const VoucherGenerate = ({ showModal, setShowModal, refreshData }) => {
  const [form, setForm] = useState({
    profile_id: "",
    pop_id: "",
    nas_id: "",
    quantity: 10,
    prefix: "",
    code_length: 7,
    suffix: "@isp.net",
    use_numbers: true,
    use_lower: false,
    use_upper: true,
    auto_password: true,
    password_length: 8,
  });

  const [preview, setPreview] = useState([]);
  const [generatedVouchers, setGeneratedVouchers] = useState([]);
  const [failedCodes, setFailedCodes] = useState([]);
  const [profilePrice, setProfilePrice] = useState(null);

  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [error, setError] = useState(null);

  const hasCharsetSelected =
    form.use_numbers || form.use_lower || form.use_upper;
  const isFormValid =
    form.profile_id && form.quantity >= 1 && hasCharsetSelected;

  // ==================== PREVIEW (LOKAL TANPA API) ====================
  const generateClientPreview = () => {
    const chars = [
      ...(form.use_numbers ? "0123456789" : ""),
      ...(form.use_lower ? "abcdefghijklmnopqrstuvwxyz" : ""),
      ...(form.use_upper ? "ABCDEFGHIJKLMNOPQRSTUVWXYZ" : ""),
    ];
    if (chars.length === 0 || form.code_length < 1) return [];

    return Array.from({ length: 5 }, () => {
      const randomCode = Array.from(
        { length: form.code_length },
        () => chars[Math.floor(Math.random() * chars.length)],
      ).join("");
      return form.prefix + randomCode + form.suffix;
    });
  };

  // Update preview secara real-time saat form berubah
  useEffect(() => {
    setPreview(generateClientPreview());
  }, [
    form.prefix,
    form.code_length,
    form.suffix,
    form.use_numbers,
    form.use_lower,
    form.use_upper,
  ]);

  // ==================== GENERATE ====================
  const handleGenerate = async () => {
    if (!isFormValid) return;

    setLoadingGenerate(true);
    setError(null);
    setFailedCodes([]);

    try {
      const data = {
        profile_id: form.profile_id,
        pop_id: form.pop_id,
        nas_id: form.nas_id,
        quantity: form.quantity,
        prefix: form.prefix,
        code_length: form.code_length,
        suffix: form.suffix,
        use_numbers: form.use_numbers,
        use_lower: form.use_lower,
        use_upper: form.use_upper,
        auto_password: form.auto_password,
        password_length: form.password_length,
        // mitra_id dan reseller_id dihilangkan
      };
      const res = await VoucherService.generate({ data: data });

      setGeneratedVouchers(res.data.data.vouchers);

      if (res.data.data.failed_codes && res.data.data.failed_codes.length > 0) {
        setFailedCodes(res.data.data.failed_codes);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || "Gagal generate voucher. Coba ulangi.";
      setError(msg);
    } finally {
      setLoadingGenerate(false);
    }
  };

  // ==================== RESET FORM ====================
  const resetForm = () => {
    setForm({
      profile_id: "",
      quantity: 10,
      prefix: "",
      code_length: 7,
      suffix: "@isp.net",
      use_numbers: true,
      use_lower: false,
      use_upper: true,
      auto_password: true,
      password_length: 8,
    });
    setPreview([]);
    setGeneratedVouchers([]);
    setFailedCodes([]);
    setProfilePrice(null);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    setShowModal(false);
  };

  // ==================== RENDER ====================
  return (
    <ModalWraper
      isOpen={showModal}
      setIsOpen={handleClose}
      title="Generate Voucher Batch"
      icon={<FaBolt className="text-cyan-400" />}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-5">
        {error && (
          <div
            className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl text-xs"
            style={{
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.2)",
            }}
          >
            <FaExclamationTriangle className="text-red-400 mt-0.5 flex-shrink-0" />
            <span className="text-red-300">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-300"
            >
              ✕
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectPop
            label="Pilih Pop"
            value={form.pop_id}
            setValue={(value) =>
              setForm((prev) => ({ ...prev, pop_id: value }))
            }
          />

          <SelectNas
            label="Pilih Nas"
            value={form.nas_id}
            setValue={(value) =>
              setForm((prev) => ({ ...prev, nas_id: value }))
            }
          />
        </div>

        <SelectVoucherProfile
          label="Pilih Profile / Paket"
          value={form.profile_id}
          setValue={(value) =>
            setForm((prev) => ({ ...prev, profile_id: value }))
          }
          setPrice={(price) => setProfilePrice(price)}
        />

        {profilePrice !== null && generatedVouchers.length === 0 && (
          <div
            className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl"
            style={{
              background: "rgba(16,185,129,0.06)",
              border: "1px solid rgba(16,185,129,0.15)",
            }}
          >
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-semibold">
                Harga Jual
              </span>
              <div className="text-sm font-bold text-emerald-400">
                {new Intl.NumberFormat("id-ID").format(profilePrice)}
              </div>
            </div>
            <div className="h-6 w-px bg-slate-700" />
            <div>
              <span className="text-[10px] text-slate-400 uppercase font-semibold">
                Estimasi Total ({form.quantity} vc)
              </span>
              <div className="text-sm font-bold text-cyan-400">
                {new Intl.NumberFormat("id-ID").format(
                  form.quantity * (profilePrice || 0),
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <FormField
            label="Jumlah Voucher"
            value={form.quantity}
            onChange={(v) =>
              setForm((prev) => ({ ...prev, quantity: parseInt(v) || 0 }))
            }
            type="number"
            placeholder="10"
            min={1}
            max={500}
            icon={FaHashtag}
            required
            disabled={generatedVouchers.length > 0}
          />
          <FormField
            label="Prefix Depan"
            value={form.prefix}
            onChange={(v) => setForm((prev) => ({ ...prev, prefix: v }))}
            placeholder="cth: VC-"
            icon={FaWandMagicSparkles}
            disabled={generatedVouchers.length > 0}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            label="Panjang Kode"
            value={form.code_length}
            onChange={(v) =>
              setForm((prev) => ({ ...prev, code_length: parseInt(v) || 0 }))
            }
            type="number"
            placeholder="7"
            min={4}
            max={32}
            icon={FaHashtag}
            suffix="karakter"
            disabled={generatedVouchers.length > 0}
          />
          <FormField
            label="Suffix Belakang"
            value={form.suffix}
            onChange={(v) => setForm((prev) => ({ ...prev, suffix: v }))}
            placeholder="@isp.net"
            disabled={generatedVouchers.length > 0}
          />
        </div>

        <div>
          <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 mb-2 uppercase tracking-wider">
            <FaShuffle className="text-[10px] text-slate-500" />
            Karakter Kode Voucher
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <CharsetToggle
              label="Angka"
              example="0-9"
              enabled={form.use_numbers}
              onToggle={(v) => setForm((prev) => ({ ...prev, use_numbers: v }))}
            />
            <CharsetToggle
              label="Huruf Kecil"
              example="a-z"
              enabled={form.use_lower}
              onToggle={(v) => setForm((prev) => ({ ...prev, use_lower: v }))}
            />
            <CharsetToggle
              label="Huruf Besar"
              example="A-Z"
              enabled={form.use_upper}
              onToggle={(v) => setForm((prev) => ({ ...prev, use_upper: v }))}
            />
          </div>
          {!hasCharsetSelected && (
            <p className="text-[10px] text-amber-400 mt-1.5">
              ⚠ Pilih minimal satu opsi karakter
            </p>
          )}
        </div>

        <div>
          <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 mb-2 uppercase tracking-wider">
            <FaWandMagicSparkles className="text-[10px] text-slate-500" />
            Preview Kode
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {preview.length > 0 ? (
              preview.map((code, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700/30"
                >
                  <span className="text-[10px] text-slate-600 w-4">
                    {i + 1}.
                  </span>
                  <span className="font-mono text-xs text-cyan-400/80 truncate">
                    {code}
                  </span>
                </div>
              ))
            ) : (
              <div className="col-span-2 px-3 py-4 text-center text-[11px] text-slate-600 rounded-lg bg-slate-800/20 border border-dashed border-slate-700/30">
                Pilih panjang kode dan karakter untuk melihat preview
              </div>
            )}
          </div>
        </div>

        {/* Generated Results */}
        {generatedVouchers.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <FaCheck className="text-[10px] text-emerald-400" />
                Hasil Generate ({generatedVouchers.length} voucher)
              </label>
              <span className="text-[10px] text-emerald-400 font-semibold">
                ✓ Sudah tersimpan di database
              </span>
            </div>

            {profilePrice !== null && (
              <div className="mb-2 text-right">
                <span className="text-[10px] text-slate-500 uppercase">
                  Total Nilai:
                </span>
                <span className="text-xs font-bold text-cyan-400 ml-2">
                  {new Intl.NumberFormat("id-ID").format(
                    generatedVouchers.length * profilePrice,
                  )}
                </span>
              </div>
            )}

            <div
              className="max-h-48 overflow-y-auto rounded-xl p-3 space-y-1"
              style={{
                background: "rgba(6,78,59,0.06)",
                border: "1px solid rgba(16,185,129,0.12)",
              }}
            >
              {generatedVouchers.map((v, i) => (
                <div
                  key={v.id}
                  className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-emerald-500/5 transition-colors"
                >
                  <span className="text-[10px] text-slate-500 w-6">
                    {i + 1}.
                  </span>
                  <span className="font-mono text-xs text-slate-200 flex-1 truncate">
                    {v.username}
                  </span>
                  <span className="font-mono text-[10px] text-slate-400 ml-3">
                    {v.password}
                  </span>
                </div>
              ))}
            </div>

            {failedCodes.length > 0 && (
              <div
                className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg text-[10px]"
                style={{
                  background: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.2)",
                }}
              >
                <FaExclamationTriangle className="text-amber-400 mt-0.5 flex-shrink-0" />
                <span className="text-amber-300">
                  {failedCodes.length} kode gagal dibuat (kemungkinan duplikat).
                </span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div
          className="flex gap-3 pt-3"
          style={{ borderTop: "1px solid rgba(51,65,85,0.2)" }}
        >
          <button
            onClick={handleClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-slate-300 hover:text-slate-100 transition-all"
            style={{
              background: "rgba(30,41,59,0.6)",
              border: "1px solid rgba(51,65,85,0.4)",
            }}
          >
            {generatedVouchers.length > 0 ? "Tutup" : "Batal"}
          </button>

          {generatedVouchers.length === 0 ? (
            <button
              onClick={handleGenerate}
              disabled={!isFormValid || loadingGenerate}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
              style={{ background: "linear-gradient(135deg,#0891b2,#0e7490)" }}
            >
              {loadingGenerate ? (
                <>
                  <FaSpinner className="text-xs animate-spin" /> Generating...
                </>
              ) : (
                <>
                  <FaBolt className="text-xs" /> Generate {form.quantity}{" "}
                  Voucher
                </>
              )}
            </button>
          ) : (
            <button
              onClick={() => {
                handleClose();
                refreshData?.();
              }}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: "linear-gradient(135deg,#059669,#047857)" }}
            >
              <FaCheck className="text-xs" /> Selesai
            </button>
          )}
        </div>
      </div>
    </ModalWraper>
  );
};
